/**
 * Markdown View - Math Rendering
 *
 * Renders LaTeX math expressions ($...$, $$...$$) in HTML elements using KaTeX.
 * Applied as a post-processing step on static cell content.
 *
 * Security: KaTeX is configured with trust:false (no arbitrary HTML/commands),
 * maxSize:10 and maxExpand:100 to prevent resource exhaustion from malicious input.
 */

import { t } from '../../../../shared/i18n/index.js';

// KaTeX is lazy-loaded on the first element that actually contains math —
// it (plus fonts/CSS) is several hundred KB that most sessions never need.
let _katex = null;
let _katexLoading = null;

function _loadKatex() {
  if (!_katexLoading) {
    _katexLoading = Promise.all([
      import('katex'),
      import('katex/dist/katex.min.css'),
    ])
      .then(([mod]) => {
        _katex = mod.default;
        return _katex;
      })
      .catch((e) => {
        _katexLoading = null; // allow retry on next call
        throw e;
      });
  }
  return _katexLoading;
}

const KATEX_OPTIONS_INLINE = {
  displayMode: false,
  throwOnError: false,
  trust: false,
  strict: false,
  maxSize: 10,
  maxExpand: 100,
};

const KATEX_OPTIONS_DISPLAY = {
  displayMode: true,
  throwOnError: false,
  trust: false,
  strict: false,
  maxSize: 10,
  maxExpand: 100,
};

/**
 * Render all LaTeX math expressions within an HTML element.
 * Walks text nodes and replaces $...$ (inline) and $$...$$ (display) with rendered math.
 * Skips content inside <code>, <pre>, and <script> elements.
 *
 * @param {HTMLElement} el - The element to process
 */
export function renderMathInElement(el) {
  if (!el) return;

  // Process display math first ($$...$$), then inline ($...$)
  // We work on innerHTML to handle math that spans across text nodes
  // (e.g., TipTap may split "$$x$$" across multiple nodes)
  const html = el.innerHTML;
  if (!html.includes('$')) return; // Fast bail if no dollar signs

  // First math on the page: load KaTeX, then re-run on this element.
  // No isConnected check — cells call this before being appended to the
  // DOM, and processHtml operates on innerHTML, which works detached.
  if (!_katex) {
    _loadKatex()
      .then(() => renderMathInElement(el))
      .catch((e) => console.warn('[Math] KaTeX failed to load:', e));
    return;
  }

  el.innerHTML = processHtml(html);
}

/**
 * Process an HTML string, rendering LaTeX math while preserving HTML structure.
 * Skips math delimiters inside <code>, <pre>, <kbd>, <script>, <style> tags.
 *
 * Display math ($$...$$) may span HTML structure — typically <br> hard breaks
 * or </p><p> paragraph boundaries — so we find those regions at the full-HTML
 * level first, flattening the inner tags into newlines for KaTeX. Inline math
 * ($...$) is still processed per text segment afterward.
 *
 * @param {string} html - Input HTML
 * @returns {string} HTML with math rendered
 */
function processHtml(html) {
  // Track which ranges are inside skip-elements (code, pre, etc.)
  const skipRegex = /<(code|pre|kbd|script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;
  let match;
  const skipRanges = [];
  while ((match = skipRegex.exec(html)) !== null) {
    skipRanges.push({ start: match.index, end: match.index + match[0].length });
  }

  function isInSkipRange(index) {
    return skipRanges.some(r => index >= r.start && index < r.end);
  }

  // --- Pass 1: display math $$...$$ across the whole HTML (may include tags) ---
  // Non-greedy match so a doc with multiple math blocks doesn't merge them.
  html = html.replace(/\$\$([\s\S]+?)\$\$/g, (full, inner, offset) => {
    if (isInSkipRange(offset)) return full;
    const latex = stripHtmlForMath(inner);
    return renderLatex(decodeEntities(latex), true);
  });

  // skipRanges may now be out-of-date after Pass 1, but they only bound code/
  // pre/etc. which were preserved verbatim. Recompute is unnecessary because
  // the skip regions themselves still match the same byte spans relative to
  // their surrounding context — Pass 2 walks text segments anyway.

  // --- Pass 2: inline math $...$ per text segment (unchanged behavior) ---
  let result = '';
  let lastIndex = 0;
  const htmlTagRegex = /<[^>]+>/g;
  const refreshedSkipRanges = [];
  let skipMatch;
  while ((skipMatch = skipRegex.exec(html)) !== null) {
    refreshedSkipRanges.push({ start: skipMatch.index, end: skipMatch.index + skipMatch[0].length });
  }
  const isInRefreshedSkip = (index) =>
    refreshedSkipRanges.some(r => index >= r.start && index < r.end);

  while ((match = htmlTagRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      const textSegment = html.slice(lastIndex, match.index);
      if (isInRefreshedSkip(lastIndex)) {
        result += textSegment;
      } else {
        result += renderInlineMath(textSegment);
      }
    }
    result += match[0];
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < html.length) {
    const textSegment = html.slice(lastIndex);
    if (isInRefreshedSkip(lastIndex)) {
      result += textSegment;
    } else {
      result += renderInlineMath(textSegment);
    }
  }
  return result;
}

/**
 * Flatten HTML structure inside a display-math region into a single LaTeX
 * string. <br> and paragraph boundaries become newlines; other tags are
 * stripped (math content shouldn't carry formatting tags). Also drops the
 * zero-width space we inject after trailing hardBreaks for display.
 */
function stripHtmlForMath(html) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p\b[^>]*>/gi, '\n')
    .replace(/<\/?p\b[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\u200B/g, '');
}

/**
 * Render inline math `$...$` in a plain text segment (no HTML tags).
 * Display math `$$...$$` is handled by the caller at the full-HTML level
 * so it can span tags (e.g., <br> for multi-line matrices).
 *
 * @param {string} text - Plain text (may contain HTML entities)
 * @returns {string} Text with inline math replaced by KaTeX HTML
 */
function renderInlineMath(text) {
  if (!text.includes('$')) return text;

  // Inline math: $...$
  // Negative lookbehind for \ (escaped dollar) and digit (price like $5)
  // Require non-space after opening $ and non-space before closing $
  return text.replace(/(?<![\\$\d])\$(?!\s)((?:[^$\\]|\\.)+?)(?<!\s)\$/g, (_, latex) => {
    return renderLatex(decodeEntities(latex), false);
  });
}

/**
 * Render a LaTeX string to HTML using KaTeX.
 * @param {string} latex - LaTeX source
 * @param {boolean} displayMode - Display (block) vs inline mode
 * @returns {string} Rendered HTML or escaped fallback on error
 */
function renderLatex(latex, displayMode) {
  latex = preprocessMath(latex, displayMode);
  try {
    return _katex.renderToString(latex, displayMode ? KATEX_OPTIONS_DISPLAY : KATEX_OPTIONS_INLINE);
  } catch (e) {
    // KaTeX with throwOnError:false shouldn't throw, but just in case
    const escaped = latex.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const cls = displayMode ? 'katex-error katex-display' : 'katex-error';
    return `<span class="${cls}" title="${t('views.md.mathError')}">${escaped}</span>`;
  }
}

/**
 * User-friendly transforms before KaTeX sees the LaTeX source:
 *
 *  1. Translate bare `;` to `&` (alignment marker). Convention: type
 *     `a ;=; b \\ c ;=; d` to align around `=`. Doesn't touch `\;` (LaTeX
 *     thin space).
 *
 *  2. Auto-wrap display-mode multi-line / aligned content in
 *     `\begin{aligned}...\end{aligned}`. KaTeX's bare `\\` outside an
 *     environment uses fixed row spacing — fine for one-line text, but
 *     tall content (fractions, integrals) overlaps. `aligned` uses
 *     LaTeX's `\jot`-based row spacing that adapts to line height, AND
 *     it's the environment where `&` alignment markers actually do
 *     something. Skip if the user already provided an environment.
 */
function preprocessMath(latex, displayMode) {
  // Inline math: leave as-is. `&` is invalid outside an environment, so
  // substituting `;` → `&` here would produce a KaTeX error. Users wanting
  // alignment should use display math (`$$...$$`).
  if (!displayMode) return latex;

  // Display mode: `;` → `&` (skipping `\;`, `\,` and other backslash escapes).
  latex = latex.replace(/(?<!\\);/g, '&');

  const hasLineBreak = latex.includes('\\\\');
  const hasAlignMarker = latex.includes('&');
  const hasEnvironment = /\\begin\s*\{[a-z*]+\}/i.test(latex);
  if ((hasLineBreak || hasAlignMarker) && !hasEnvironment) {
    return `\\begin{aligned}${latex}\\end{aligned}`;
  }
  return latex;
}

/**
 * Decode common HTML entities back to characters for KaTeX parsing.
 * TipTap/markdown conversion may encode characters that LaTeX needs.
 * @param {string} str
 * @returns {string}
 */
function decodeEntities(str) {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
